/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { logger } from "@/lib/utils/logger";
import { toast } from "sonner";
import type { ApiFailure } from "./api-client";

/**
 * Get a user-friendly error description based on failure status/code
 */
function getErrorDescription(
  prefix: string,
  failure: ApiFailure,
): { title: string; description: string; retryable: boolean } {
  const status = failure.status;
  const code = failure.code;

  // Network errors (status 0 or NETWORK_ERROR code)
  if (status === 0 || code === "NETWORK_ERROR") {
    return {
      title: "No Internet Connection",
      description:
        "Your changes weren't saved. Check your connection and try again.",
      retryable: true,
    };
  }

  // Timeout
  if (status === 408 || code === "TIMEOUT") {
    return {
      title: "Taking Too Long",
      description:
        "The server didn't respond in time. Hit retry — your data is safe.",
      retryable: true,
    };
  }

  // Rate limited
  if (status === 429) {
    return {
      title: "Slow Down",
      description:
        "Too many changes at once. Wait a few seconds and try again.",
      retryable: true,
    };
  }

  // Auth errors
  if (status === 401 || code === "UNAUTHORIZED") {
    return {
      title: "You've Been Logged Out",
      description: "Your session expired. Log back in to keep working.",
      retryable: false,
    };
  }

  // Forbidden
  if (status === 403) {
    return {
      title: "No Access",
      description:
        "You don't have permission for this. Ask your admin if you need access.",
      retryable: false,
    };
  }

  // Not found
  if (status === 404) {
    return {
      title: prefix,
      description:
        failure.message ||
        "This item may have been deleted or moved. Refresh the page and try again.",
      retryable: false,
    };
  }

  // Validation errors (422)
  if (status === 422) {
    return {
      title: prefix,
      description:
        failure.message ||
        "Something doesn't look right. Double-check your entries and try again.",
      retryable: false,
    };
  }

  // Server errors (5xx)
  if (status >= 500) {
    return {
      title: "Something Went Wrong",
      description:
        "This is on our end, not yours. Your data is safe — try again in a moment.",
      retryable: true,
    };
  }

  // Fallback with the actual message from the API
  return {
    title: prefix,
    description: failure.message || "Something unexpected happened. Try again.",
    retryable: true,
  };
}

/**
 * Display a toast notification for an API failure
 *
 * @param prefix - Error message prefix (e.g., "Failed to create project")
 * @param failure - The ApiFailure object from the API client
 * @param onRetry - Optional retry callback. If provided and the error is retryable, a Retry button is shown.
 */
export function toastFromApiFailure(
  prefix: string,
  failure: ApiFailure,
  onRetry?: () => void | Promise<void>,
): void {
  // Don't show error toasts for transient failures that are being retried server-side
  // These errors are expected to succeed on retry and showing errors confuses users
  const isTransient =
    failure.details &&
    typeof failure.details === "object" &&
    "transient" in failure.details &&
    failure.details.transient === true;

  const serverRetriesExhausted =
    failure.details &&
    typeof failure.details === "object" &&
    "serverRetriesExhausted" in failure.details &&
    failure.details.serverRetriesExhausted === true;

  // Only suppress toasts if server-side retries were attempted but not exhausted
  // If server retries are exhausted, we should still show the error
  if (isTransient && !serverRetriesExhausted) {
    // Log silently - don't show toast for transient failures that are being handled
    logger.info(`Suppressing error toast for transient failure: ${prefix}`, {
      code: failure.code,
      status: failure.status,
      requestId: failure.requestId,
    });
    return;
  }

  const { title, description, retryable } = getErrorDescription(
    prefix,
    failure,
  );

  const action =
    retryable && onRetry ? { label: "Retry", onClick: onRetry } : undefined;

  toast.error(title, {
    description,
    action: action
      ? { label: action.label, onClick: action.onClick }
      : undefined,
  });
}

/**
 * Unified error handler that works with both ApiFailure and generic errors
 *
 * This function provides consistent error handling across the application:
 * - Handles ApiFailure from API client responses
 * - Handles generic Error objects
 * - Handles unknown error types
 * - Provides appropriate user-facing messages
 * - Logs errors for debugging
 *
 * @param error - The error to handle (ApiFailure, Error, or unknown)
 * @param prefix - Optional prefix for error messages (default: "Operation failed")
 * @param options - Optional configuration
 *
 * @example
 * ```ts
 * const result = await api.post('/api/projects', data);
 * if (isApiFailure(result)) {
 *   handleApiError(result, "Failed to create project");
 *   return;
 * }
 * ```
 *
 * @example
 * ```ts
 * try {
 *   await someOperation();
 * } catch (error) {
 *   handleApiError(error, "Operation failed");
 * }
 * ```
 */
export function handleApiError(
  error: ApiFailure | Error | unknown,
  prefix: string = "Operation failed",
  options?: {
    /**
     * Whether to log the error (default: true)
     */
    log?: boolean;
    /**
     * Optional retry callback. Shows a Retry button on the error toast for retryable errors.
     */
    onRetry?: () => void | Promise<void>;
  },
): void {
  const shouldLog = options?.log !== false;
  const onRetry = options?.onRetry;

  // Handle ApiFailure
  if (
    typeof error === "object" &&
    error !== null &&
    "ok" in error &&
    (error as { ok?: boolean }).ok === false
  ) {
    const failure = error as ApiFailure;
    if (shouldLog) {
      logger.error("API failure:", {
        prefix,
        status: failure.status,
        code: failure.code,
        message: failure.message,
        requestId: failure.requestId,
      });
    }
    toastFromApiFailure(prefix, failure, onRetry);
    return;
  }

  // Handle Error objects
  if (error instanceof Error) {
    if (shouldLog) {
      logger.error("Error:", {
        prefix,
        error: error.message,
        stack: error.stack,
      });
    }

    // Network errors
    if (error.message.match(/network|fetch|failed to fetch/i)) {
      toast.error("No Internet Connection", {
        description:
          "Your changes weren't saved. Check your connection and try again.",
        action: onRetry ? { label: "Retry", onClick: onRetry } : undefined,
      });
      return;
    }

    // Timeout errors
    if (error.message.match(/timeout|timed?\s*out|aborted/i)) {
      toast.error("Taking Too Long", {
        description:
          "The server didn't respond in time. Hit retry — your data is safe.",
        action: onRetry ? { label: "Retry", onClick: onRetry } : undefined,
      });
      return;
    }

    // Unauthorized/Forbidden errors
    if (
      error.message.match(
        /\bunauthorized\b|\bforbidden\b|\bsession expired\b/i,
      ) ||
      error.message.match(/\b401\b.*error|\berror.*\b401\b|^401$/i) ||
      error.message.match(/\b403\b.*error|\berror.*\b403\b|^403$/i)
    ) {
      toast.error("You've Been Logged Out", {
        description: "Your session expired. Log back in to keep working.",
      });
      return;
    }

    // Rate limit errors
    if (
      error.message.match(/rate.?limit/i) ||
      error.message.match(/\b429\b.*error|\berror.*\b429\b|^429$/i)
    ) {
      toast.error("Slow Down", {
        description:
          "Too many changes at once. Wait a few seconds and try again.",
      });
      return;
    }

    // Server errors
    if (
      error.message.match(/internal.?server/i) ||
      error.message.match(/\b500\b.*error|\berror.*\b500\b|^500$/i)
    ) {
      toast.error("Something Went Wrong", {
        description:
          "This is on our end, not yours. Your data is safe — try again in a moment.",
        action: onRetry ? { label: "Retry", onClick: onRetry } : undefined,
      });
      return;
    }

    // Generic error with the actual message
    toast.error(prefix, {
      description: error.message,
      action: onRetry ? { label: "Retry", onClick: onRetry } : undefined,
    });
    return;
  }

  // Handle unknown errors
  if (shouldLog) {
    logger.error("Unknown error:", { prefix, error: String(error) });
  }
  toast.error(prefix, {
    description: "Something unexpected happened. Try again.",
    action: onRetry ? { label: "Retry", onClick: onRetry } : undefined,
  });
}
